import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';

import routes from 'routes';

import Label from 'components/common/Label';
import DropDown from 'components/common/Dropdown';
import Proptypes from 'prop-types';
import api from 'services/api';

import './style.scss';

// To draw the method label specify the position
const Table = ({
  headerAttributes,
  drawMethodPosition,
  drawIpAddressPosition,
  drawURIPosition,
  drawAddToGroupPosition,
  data = [],
  hasMore,
  callBackUrl,
  sortableColumns = [],
  sortColumn,
  sortOrder,
  onSort,
  renderExpandedRow,
}) => {
  const [stateData, setStateData] = useState(data);
  const [expandedRows, setExpandedRows] = useState(new Set());

  useEffect(() => {
    setStateData(data);
  }, [stateData, data]);

  const drawHeader = (items) => {
    return items.map((item, index) => {
      let className = '';

      switch (item) {
        case 'URI':
          className = 'table-uri-header-field';
          break;
        case 'IP Address':
          className = 'table-ip-header-field';
          break;
        case 'Method':
          className = 'table-method-header-field';
          break;
        case ' ':
          className = 'table-label-header-field';
          break;
        default:
          break;
      }

      const sortableColumn = sortableColumns[index];
      const isSorted = sortableColumn && sortableColumn === sortColumn;

      return (
        <th className={className + ' has-text-centered'} key={item}>
          {sortableColumn ? (
            <button
              type="button"
              className="table-sort-button"
              onClick={() => onSort(sortableColumn)}
              aria-label={`Sort by ${item}`}
            >
              {item}
              {isSorted && (
                <i
                  className={`fas ${sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'} ml-1`}
                  aria-hidden="true"
                />
              )}
            </button>
          ) : item}
        </th>
      );
    });
  };

  const drawMethodLabel = (method) => {
    switch (method) {
      case 'GET':
        return (
          <Label color="#26AA6C" textColor="white">
            {method}
          </Label>
        );
      case 'POST':
        return (
          <Label color="#FFC857" textColor="black">
            {method}
          </Label>
        );
      case 'PUT':
        return (
          <Label color="#00B4D8" textColor="white">
            {method}
          </Label>
        );
      case 'DELETE':
        return (
          <Label color="#DF2B2B" textColor="white">
            {method}
          </Label>
        );
      default:
        return <Label>{method}</Label>;
    }
  };

  const drawAddToGroup = (groupData) => {
    const pairs = [];
    for (const group of groupData.groups) {
      pairs.push({
        name: group.name,
        value: group.id,
      });
    }
    const addToBotsList = (pair, setIsVisible) => {
      api.addToBots(
        groupData.logger,
        { id: groupData.requestId, groupId: pair.value },
        (res) => {
          if (res.data.status === 200) {
            setIsVisible(false);
          } else if (res.data.status === 409) {
            // Conflict with trying to add to a group a user who already has one
            setIsVisible(false);
          }
        }
      );
    };
    return (
      <DropDown
        visible={groupData.curGroup === 'None'}
        nameValuePairs={pairs}
        callBackOnSelect={addToBotsList}
      />
    );
  };
  const drawData = (items) => {
    if (!items) {
      return;
    }
    return items.map((item, rowIndex) => {
      const rowId = item[0] && item[0].id ? item[0].id : rowIndex;
      const isExpanded = expandedRows.has(rowId);
      const toggleRow = () => {
        setExpandedRows(previous => {
          const next = new Set(previous);
          if (next.has(rowId)) {
            next.delete(rowId);
          } else {
            next.add(rowId);
          }
          return next;
        });
      };

      return (
        <React.Fragment key={rowId}>
          <tr
            aria-expanded={renderExpandedRow ? isExpanded : undefined}
            onClick={renderExpandedRow ? toggleRow : undefined}
            onKeyDown={renderExpandedRow ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                toggleRow();
              }
            } : undefined}
            role={renderExpandedRow ? 'button' : undefined}
            tabIndex={renderExpandedRow ? 0 : undefined}
          >
            {item.map((column, colIndex) => {
              if (colIndex === 0) {
                // first is id
                return null;
              }
              if (colIndex === drawMethodPosition) {
                return <td key={colIndex}>{drawMethodLabel(column)}</td>;
              }
              if (colIndex === drawURIPosition) {
                return (
                  <td
                    title={column}
                    className="table-uri-data-field"
                    key={colIndex}
                  >
                    {column}
                  </td>
                );
              }
              if (colIndex === drawIpAddressPosition) {
                const { ipAddress, requesterId, loggerId } = column;
                const link = routes.requestersRequests.ref(loggerId, requesterId);
                return (
                  <td
                    title={ipAddress}
                    className="table-ip-data-field"
                    key={colIndex}
                  >
                    <Link to={link} onClick={event => event.stopPropagation()}>{ipAddress}</Link>
                  </td>
                );
              }
              if (colIndex === drawAddToGroupPosition) {
                return <td key={colIndex}>{drawAddToGroup(column)}</td>;
              }
              return <td key={colIndex}>{column}</td>;
            })}
          </tr>
          {renderExpandedRow && isExpanded && (
            <tr className="table-expanded-row">
              <td className="table-expanded-cell has-text-left" colSpan={headerAttributes.length}>
                {renderExpandedRow(item[0])}
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <div className="card table-container">
      <InfiniteScroll
        dataLength={stateData.length}
        next={callBackUrl}
        hasMore={hasMore}
        loader={<h4 className="has-text-centered">Loading...</h4>}
        scrollableTarget="layout-scroll"
      >
        <table className="table my-table has-text-centered is-fullwidth">
          <thead className="table-header">
            <tr>{drawHeader(headerAttributes)}</tr>
          </thead>
          <tbody className="table-body">{drawData(stateData)}</tbody>
        </table>
      </InfiniteScroll>
    </div>
  );
};

Table.propTypes = {
  headerAttributes: Proptypes.arrayOf(Proptypes.string),
  drawMethodPosition: Proptypes.number,
  drawURIPosition: Proptypes.number,
  drawIpAddressPosition: Proptypes.number,
  drawAddToGroupPosition: Proptypes.number,
  data: Proptypes.arrayOf(Proptypes.array),
  hasMore: Proptypes.bool,
  callBackUrl: Proptypes.func,
  sortableColumns: Proptypes.arrayOf(Proptypes.oneOfType([
    Proptypes.string,
    Proptypes.oneOf([null]),
  ])),
  sortColumn: Proptypes.string,
  sortOrder: Proptypes.oneOf(['asc', 'desc']),
  onSort: Proptypes.func,
  renderExpandedRow: Proptypes.func,
};

export default Table;
